import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import wsClient from "src/wsClient"
import {
  openCityModal,
  selectSelectedCity,
  startColonizationFromCity,
  startMovementFromCity,
  startPickingOrigin,
  startStrikeFromCity,
} from "src/store/ui"
import {
  selectCityMetadataByUser,
  selectGame,
  selectMetadataCells,
  selectViewerId,
} from "src/store/game"
import { selectGameConfig } from "src/store/common"
import { cellDisplayName, isWater, movementsTouching } from "src/utils/maps"
import { cellActions } from "src/utils/cellActions"
import useShortcuts from "src/hooks/useShortcuts"
import ModalService from "src/modals"
import { playUiSound } from "src/utils/audio"

/* The orders a selected cell admits, and the buttons that give them — ONE copy for the two places
   that show them: the map's action bar, and the city sheet on the phone (owner, 2026-09-30: "keep
   the buttons for the mobile version"). The rules are `utils/cellActions`; this is what turns its
   entries into presses, so a button in the sheet and the same button on the bar cannot disagree
   about its label, its price, its reason or what it does. */

export const ICONS = {
  march: "⚔",
  marchTo: "⚔",
  colonize: "🧭",
  settle: "🚩",
  spy: "🔍",
  strike: "🚀",
  city: "🏛",
  battle: "🔥",
  tunnelLoad: "🕳",
  tunnelUnload: "🕳",
  discharge: "🪖",
  cancelSettle: "✕",
}

/* Orders that take the player to the map — drawing a route, picking an origin, aiming the silo.
   The city sheet stands between the player and the map, so these close it: the bar's keys fire
   them with the sheet open too, and a route drawn under a sheet is a route nobody can see. */
const LEAVES_FOR_THE_MAP = new Set(["march", "marchTo", "colonize", "strike"])

/* A press makes the sound of what it did. The ones that open a modal are silent here:
   `ModalService` already sounds an opening, and two blips for one press is a stutter. */
const SOUND_FOR = {
  march: "march",
  marchTo: "march",
  strike: "march",
  colonize: "march",
  // The orders that leave on the socket (settle, spy, share…) are sounded there, by `orderSounds`.
}

/** The selected cell's orders: `cellActions`' answer, plus a handler for every key and the cell's
 *  name — or null with nothing selected. */
export const useCellOrders = () => {
  const dispatch = useDispatch()
  const city = useSelector(selectSelectedCity)
  const cityMetadata = useSelector((state) => city && selectCityMetadataByUser(state, city))
  const cells = useSelector(selectMetadataCells)
  const userId = useSelector(selectViewerId)
  const gameConfig = useSelector(selectGameConfig)
  const game = useSelector(selectGame)

  if (!city) return null

  const users = game?.metadata?.users || {}
  const userMetadata = users[String(userId)]
  // A sea cell selects since 2026-09-08 (03/D2): a march pauses on it while it saves the points
  // to cross, and the stack standing there is what the player clicked. The sea is never land to
  // colonise or found on, and the bar says so instead of quoting a price for it.
  const isLand = !isWater({ column: city.col, row: city.row })
  const result = cellActions({
    coords: city,
    cellMetadata: cityMetadata,
    userId,
    userMetadata,
    cells,
    users,
    rules: gameConfig?.rules_config,
    gameConfig,
    tick: game?.tick,
    isLand,
    movementsHere: movementsTouching(game?.metadata?.movements, city, {
      userId,
      teamOf: (id) => users[String(id)]?.team,
    }).length,
    movements: game?.metadata?.movements,
    battles: game?.metadata?.battles,
    upgrades: game?.metadata?.upgrades,
    foundings: game?.metadata?.foundings,
    tunnelOrders: game?.metadata?.tunnel_orders,
  })
  const name = cellDisplayName({ row: city.row, column: city.col, held: result.isOwn })
  const at = { col: city.col, row: city.row }

  const handlers = {
    march: () => dispatch(startMovementFromCity(city)),
    marchTo: () => dispatch(startPickingOrigin()),
    // The founding party is already picked (`cellActions.colonizingPartyFor`), so this skips the
    // troop picker and goes straight to the route. A helper, not an order the server knows: the
    // march is a plain `move_troops`, and Settle on arrival is still the player's press.
    colonize: () => {
      const order = result.actions.find((a) => a.key === "colonize")
      if (order?.troops) dispatch(startColonizationFromCity({ ...at, troops: order.troops }))
    },
    settle: () => wsClient.send("game", { action: "found_settlement", column: city.col, row: city.row }),
    cancelSettle: () => wsClient.send("game", { action: "cancel_founding", column: city.col, row: city.row }),
    strike: () => dispatch(startStrikeFromCity(city)),
    // UIWatcher mounts the modal — it watches this flag for every route into the city view.
    city: () => dispatch(openCityModal()),
    battle: () => ModalService.show("BattleModal", { city: at }),
    tunnelLoad: () => ModalService.show("TunnelModal", { city: at, mode: "load" }),
    tunnelUnload: () => ModalService.show("TunnelModal", { city: at, mode: "unload" }),
    discharge: () => ModalService.show("DischargeModal", { city: at }),
    share: () =>
      wsClient.send("game", {
        action: "send_chat_message",
        message: `📍 ${name} · ${city.col}-${city.row}`,
        message_type: "team",
      }),
  }

  // One wrap rather than a call inside each handler, and it covers the keyboard too — the shortcut
  // path fires `handlers[key]` as well.
  Object.keys(handlers).forEach((key) => {
    const run = handlers[key]
    handlers[key] = (...args) => {
      if (SOUND_FOR[key]) playUiSound(SOUND_FOR[key])
      if (LEAVES_FOR_THE_MAP.has(key) && ModalService.isShowing("CityModal")) ModalService.hide("CityModal")
      return run(...args)
    }
  })

  return { city, name, isLand, users, userMetadata, handlers, ...result }
}

/** The buttons for `actions`, as the bar draws them. `onHint(reason | null)` hears the refusal of
 *  whichever button the pointer or the focus is on. */
export const CellOrderButtons = ({ actions, handlers, onHint = () => {} }) => {
  const { t } = useTranslation()
  const shortcuts = useShortcuts()

  // A priced order shows its price where every other button shows its icon (owner, 2026-09-09).
  // The cost is the thing the player is deciding about, and as a "· 300 🥩" tail it sat at the end
  // of the longest label on the bar, under an emoji that said nothing — the compass was read as a
  // clock. The price still carries its unit (owner's Q3): "400" alone left the player guessing
  // whether it cost workers or meat, and since Q23/Q28 the answer differs per team. Colonize
  // quotes the same coin as Settle, because it is what Settle will charge on arrival.
  const priceLabel = (action) =>
    action.cost
      ? t(action.currency === "workers" ? "game.mapAction.priceWorkers" : "game.mapAction.priceMeat", {
          cost: action.cost,
        })
      : null
  const reasonText = (action) => (action.reason ? t(`game.mapAction.reason.${action.reason.key}`, action.reason) : null)
  // What an enabled button will do, where that is not obvious from its name: Colonize picks the
  // party and leaves the founding for arrival, which is worth saying before the press.
  const titleText = (action) => {
    if (action.key !== "colonize" || !action.enabled) return null
    const [type, number] = Object.entries(action.troops || {})[0] || []
    return t(action.currency === "workers" ? "game.mapAction.colonizeTitleWorkers" : "game.mapAction.colonizeTitleMeat", {
      count: number,
      unit: t(`city.units.${type}`, { defaultValue: type }),
      cost: action.cost,
    })
  }

  return actions.map((action) => {
    const reason = reasonText(action)
    const key = shortcuts[action.key] || (action.key === "marchTo" && shortcuts.march)
    return (
      <button
        key={action.key}
        type="button"
        // What basic training's hand points at (`tutorial/pointer.js`).
        data-action={action.key}
        // Not the `disabled` attribute: a disabled button gets no mouse events, and the reason is
        // shown on hover. aria-disabled keeps the semantics for assistive tech.
        className={`map-action${action.enabled ? "" : " disabled"}`}
        aria-disabled={!action.enabled}
        title={reason || titleText(action) || undefined}
        // A disabled press says why — on a phone there is no hover to say it.
        onClick={action.enabled ? handlers[action.key] : () => onHint(reason)}
        onMouseEnter={() => onHint(reason)}
        onMouseLeave={() => onHint(null)}
        onFocus={() => onHint(reason)}
        onBlur={() => onHint(null)}
      >
        <span className={`map-action-icon${priceLabel(action) ? " price" : ""}`}>
          {priceLabel(action) ?? ICONS[action.key]}
        </span>
        {t(`game.mapAction.${action.key}`)}
        {key && <kbd className="map-action-key">{key.toUpperCase()}</kbd>}
      </button>
    )
  })
}
